import * as React from 'react'

import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { toggleClass } from 'react-classlist-helper'

import { IWhiteListItem } from '~/client/graph'
import { Loader } from '~/client/src/shared/components/Loader'
import { ISharedProps } from '~/client/src/shared/components/StruxhubInputs/BaseStruxhubInput'
import StruxhubTagsAsTextInput from '~/client/src/shared/components/StruxhubInputs/StruxhubTagsAsTextInput/StruxhubTagsAsTextInput'
import { TagIconType } from '~/client/src/shared/enums/TagIcon'
import WhiteListItemsStore from '~/client/src/shared/stores/domain/WhiteListItems.store'
import {
  DOMAIN_VALIDATOR_PATTERN,
  WITHOUT_SPECIAL_CHARACTERS_EXCEPT_DOT_DASH_PATTERN,
} from '~/client/src/shared/utils/regExpPatterns'

interface IProps extends ISharedProps {
  companyId: string
  isLoading?: boolean
  searchKey?: string

  whiteListItemsStore?: WhiteListItemsStore
}

const domainIsNotValid = `The domain format is not valid. Try something like 'gmail.com'`
const domainIsAlreadyAdded = 'The domain is already added'

@inject('whiteListItemsStore')
@observer
export default class CompanyDomainsInput extends React.Component<IProps> {
  @observable private _isLoading: boolean = false

  @observable private isValid: boolean = true
  @observable private validationMessage: string = ''
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public async componentDidMount() {
    this._isLoading = true
    try {
      if (this.companyId) {
        await this.props.whiteListItemsStore.requestItemsByCompany(
          this.companyId,
        )
      } else {
        await this.props.whiteListItemsStore.requestAllItems()
      }
    } finally {
      runInAction(() => {
        this._isLoading = false
      })
    }
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (this.localProps.companyId !== this.props.companyId) {
        this.localProps.companyId = this.props.companyId
      }
      if (this.localProps.searchKey !== this.props.searchKey) {
        this.localProps.searchKey = this.props.searchKey
      }
      if (this.localProps.isLoading !== this.props.isLoading) {
        this.localProps.isLoading = this.props.isLoading
      }
    })
  }

  private get companyId(): string {
    return this.localProps.companyId
  }

  private get isLoading(): boolean {
    return this.localProps.isLoading || this._isLoading
  }

  private get wlItems(): IWhiteListItem[] {
    return this.localProps.whiteListItemsStore.getItemsByCompanyId(
      this.companyId,
    )
  }

  @computed
  private get domains(): string[] {
    return this.wlItems.map(d => d.domainName)
  }

  public render() {
    return (
      <div className="tw:relative">
        <div className={toggleClass('tw:blur-[1px]', this.isLoading)}>
          <StruxhubTagsAsTextInput
            {...this.props}
            iconName={TagIconType.At}
            values={this.domains}
            onChange={this.handleChange}
            onInputChange={this.handleOnInputChange}
            isValid={this.isValid}
            validationMessage={this.validationMessage}
          />
        </div>
        {this.isLoading && (
          <Loader className="tw:absolute tw:right-[50%] tw:top-[30%] tw:flex tw:items-center" />
        )}
      </div>
    )
  }

  @action.bound
  private handleChange(newDomains: string[]) {
    if (newDomains.length === this.domains.length) {
      return
    }

    if (newDomains.length > this.domains.length) {
      this.handleAddDomain(newDomains[newDomains.length - 1])
    } else {
      const wlItemsToDelete = this.wlItems.filter(
        d => !newDomains.includes(d.domainName),
      )
      this.handleDeleteDomains(wlItemsToDelete)
    }
  }

  @action.bound
  private async handleAddDomain(newDomain: string) {
    if (!this.isValid) {
      return
    }

    this._isLoading = true
    try {
      await this.props.whiteListItemsStore.addItem(newDomain, this.companyId)
    } catch (e) {
      this.setError(e.message)
    } finally {
      this._isLoading = false
    }
  }

  @action.bound
  private async handleDeleteDomains(wlItems: IWhiteListItem[]) {
    this._isLoading = true

    try {
      await this.props.whiteListItemsStore.deleteItems(wlItems)
    } catch (e) {
      this.setError(e.message)
    } finally {
      this._isLoading = false
    }
  }

  @action.bound
  private validate(domain: string) {
    this.resetError()

    if (!DOMAIN_VALIDATOR_PATTERN.test(domain)) {
      this.setError(domainIsNotValid)
    }

    if (this.domains.includes(domain)) {
      this.setError(domainIsAlreadyAdded)
    }
  }

  @action.bound
  private setError(message: string) {
    this.isValid = false
    this.validationMessage = message
  }

  @action.bound
  private resetError() {
    this.isValid = true
    this.validationMessage = ''
  }

  @action.bound
  private handleOnInputChange(rawDomain: string): string {
    const formattedDomain = rawDomain.replace(
      WITHOUT_SPECIAL_CHARACTERS_EXCEPT_DOT_DASH_PATTERN,
      '',
    )

    this.validate(formattedDomain)

    return formattedDomain
  }
}
